import type { CSSProperties, MouseEventHandler, ReactNode } from "react";
import Link from "@/components/LocalizedLink";
import { getCtaLabel, siteContent, type SiteCta } from "@/content/site";
import { useTranslation } from "@/i18n/useTranslation";

type PrimaryCtaLinkProps = {
  cta?: SiteCta;
  labelVariant?: "label" | "shortLabel";
  className?: string;
  children?: ReactNode;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  style?: CSSProperties;
  dataEvent?: string;
  dataEventLocation?: string;
  dataEventSurface?: string;
  dataEventTarget?: string;
  dataEventServiceSlug?: string;
  dataEventIndustrySlug?: string;
  dataEventArticleSlug?: string;
  ariaLabel?: string;
};

const PrimaryCtaLink = ({
  cta = siteContent.ctas.primary,
  labelVariant = "label",
  className,
  children,
  onClick,
  style,
  dataEvent,
  dataEventLocation,
  dataEventSurface,
  dataEventTarget,
  dataEventServiceSlug,
  dataEventIndustrySlug,
  dataEventArticleSlug,
  ariaLabel,
}: PrimaryCtaLinkProps) => {
  const { locale } = useTranslation();
  const label = children ?? getCtaLabel(cta, locale, labelVariant);
  const resolvedDataEvent = dataEvent ?? (cta === siteContent.ctas.secondary ? "secondary_cta_click" : "primary_cta_click");
  const resolvedDataEventTarget = dataEventTarget ?? (cta.external ? "booking" : cta.destination);
  const analyticsAttributes = {
    "data-event": resolvedDataEvent,
    "data-event-location": dataEventLocation,
    "data-event-surface": dataEventSurface,
    "data-event-target": resolvedDataEventTarget,
    "data-event-service-slug": dataEventServiceSlug,
    "data-event-industry-slug": dataEventIndustrySlug,
    "data-event-article-slug": dataEventArticleSlug,
  };

  if (cta.external) {
    return (
      <a
        href={cta.destination}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        onClick={onClick}
        style={style}
        aria-label={ariaLabel}
        {...analyticsAttributes}
      >
        {label}
      </a>
    );
  }

  return (
    <Link
      to={cta.destination}
      className={className}
      onClick={onClick}
      style={style}
      aria-label={ariaLabel}
      {...analyticsAttributes}
    >
      {label}
    </Link>
  );
};

export default PrimaryCtaLink;
